import { memo, useMemo, useCallback } from 'react';
import { Button, Spinner } from '@monksflow/monks-ui';
import { ImageIcon, RocketIcon, UsersIcon } from 'lucide-react';
import ArrowRightAltIcon from '@/shared/ui/Icons/ArrowRightAltIcon';
import { useGetDownloadUrl } from '@/shared/hooks/useGetDownloadUrl';
import type {
  IdeasShowcaseProps,
  IdeaShowcaseCardProps,
  IdeaShowcaseScoresProps,
} from './IdeasShowcase.types';
import { getWidgetStores } from '@/entities/widget';
import { MotionBox } from '@/shared/ui/motion';
import { useTranslation } from 'react-i18next';

const SCORE_CARD = 'flex flex-1 flex-col gap-y-10 rounded-3xl bg-surface p-10';

/**
 * IdeaShowcaseScores - Displays audience and creativity scores
 * Memoized to prevent unnecessary re-renders when parent updates
 */
const IdeaShowcaseScores = memo(function IdeaShowcaseScores({
  audienceDescription,
  creativityDescription,
}: IdeaShowcaseScoresProps) {
  const { t } = useTranslation('contentTest');

  return (
    <div className="flex w-full gap-4">
      {/* Audience Score Card */}
      <div className={SCORE_CARD}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-row gap-2">
            <UsersIcon className="size-8 shrink-0 text-muted-foreground" aria-hidden="true" />
            <p className="text-heading-xl text-foreground">
              {t('ideationWidget.ideasShowcase.audience')}
            </p>
          </div>
          <p className="text-body-lg text-foreground">{audienceDescription}</p>
        </div>
      </div>

      {/* Creativity Score Card */}
      <div className={SCORE_CARD}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-row items-start gap-2">
            <RocketIcon className="size-8 shrink-0 text-muted-foreground" aria-hidden="true" />
            <p className="text-heading-xl text-foreground">
              {t('ideationWidget.ideasShowcase.creativity')}
            </p>
          </div>
          <p className="text-body-lg text-foreground">{creativityDescription}</p>
        </div>
      </div>
    </div>
  );
});

interface IdeaImageProps {
  assetId: string;
}

const IDEA_IMAGE_PLACEHOLDER =
  'flex size-full items-center justify-center bg-background/10 text-background/30';

/**
 * IdeaImage - Async image loader for idea cards
 * Memoized to prevent re-fetching on parent re-renders
 */
const IdeaImage = memo(function IdeaImage({ assetId }: IdeaImageProps) {
  const { downloadUrl, isLoading } = useGetDownloadUrl(assetId);

  if (isLoading) {
    return (
      <div className={`${IDEA_IMAGE_PLACEHOLDER} text-background/50`}>
        <Spinner size="xl" />
      </div>
    );
  }

  if (!downloadUrl) {
    return (
      <div className={IDEA_IMAGE_PLACEHOLDER}>
        <ImageIcon className="size-16" />
      </div>
    );
  }

  return <img src={downloadUrl} alt="" className="size-full object-cover" />;
});

/**
 * IdeaShowcaseCard - Individual idea card with scores
 * Memoized to prevent re-renders when other ideas or parent state changes
 */
const IdeaShowcaseCard = memo(function IdeaShowcaseCard({
  idea,
  onExploreIdea,
  imagePosition,
}: IdeaShowcaseCardProps) {
  const { t } = useTranslation('contentTest');

  // Memoize score descriptions to avoid recalculating on each render
  const scoreData = useMemo(() => {
    const audienceDescription =
      idea.audienceScore.find((s) => s.title === 'General')?.description ||
      idea.audienceScore[0]?.description;

    const creativityDescription =
      idea.creativeScore.find((s) => s.title === 'General')?.description ||
      idea.creativeScore[0]?.description;

    return {
      audienceDescription,
      creativityDescription,
    };
  }, [idea.audienceScore, idea.creativeScore]);

  const { sidePanelLayoutMode } = getWidgetStores().uiStore.useSidePanelLayoutMode();
  const isFullscreen = sidePanelLayoutMode === 'fullscreen';

  const flexDirection = useMemo(() => {
    if (!isFullscreen) return 'flex-col';
    return imagePosition === 'left' ? 'flex-row' : 'flex-row-reverse';
  }, [isFullscreen, imagePosition]);

  // Memoize image asset ID to prevent unnecessary prop changes
  const imageAssetId = idea.imageAssetId;

  return (
    <div className="flex w-full flex-col gap-4 px-6">
      {/* Main Dark Card */}
      <div className={`flex gap-12 rounded-3xl bg-background-emphasis p-10 ${flexDirection}`}>
        {/* Left Content */}
        <div className="flex flex-1 flex-col justify-center gap-8">
          {/* Body Content */}
          <div className="flex flex-col gap-4">
            {/* Headline */}
            <div className="flex flex-col gap-4">
              <p className="text-heading-xl text-muted-foreground">{idea.title}</p>
              <p className="text-body-lg text-background">{idea.description}</p>
            </div>

            {/* Provocation */}
            <div className="flex flex-col gap-2">
              <p className="text-body-lg font-medium text-muted-foreground">
                {t('ideationWidget.ideasShowcase.provocation')}
              </p>
              <p className="text-body-lg text-background">{idea.provocation}</p>
            </div>
          </div>

          <Button variant="secondary" size="default" className="self-start" onClick={onExploreIdea}>
            {t('ideationWidget.ideasShowcase.exploreIdea')}
            <ArrowRightAltIcon className="size-4" />
          </Button>
        </div>

        <MotionBox
          layout
          // Fullscreen sizes the image as the 580px design width's share of the
          // 1440px design viewport, so it tracks the window; the side panel just
          // fills its column.
          className={`flex shrink-0 items-center justify-center overflow-hidden rounded-3xl bg-background/10 ${
            isFullscreen ? 'h-81.5 w-[40.28vw]' : 'h-91.5 w-full'
          }`}
        >
          {imageAssetId ? (
            <IdeaImage assetId={imageAssetId} />
          ) : (
            <ImageIcon className="size-16 text-background/30" aria-hidden="true" />
          )}
        </MotionBox>
      </div>

      {/* Score Cards */}
      <IdeaShowcaseScores
        audienceDescription={scoreData.audienceDescription}
        creativityDescription={scoreData.creativityDescription}
      />
    </div>
  );
});

/**
 * IdeasShowcase - Main container for displaying all ideas
 * Uses memoized child components to prevent unnecessary re-renders
 */
export const IdeasShowcase = memo(function IdeasShowcase({
  ideas,
  onIdeaSelect,
  exporting = false,
}: IdeasShowcaseProps) {
  const { t } = useTranslation('contentTest');

  // Memoize callback factory to prevent creating new functions on each render
  const handleIdeaSelect = useCallback(
    (index: number) => {
      onIdeaSelect(index);
    },
    [onIdeaSelect],
  );

  if (ideas.length === 0) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-4 px-15 py-20">
      {/* Section Title */}
      <div className="flex h-18 flex-col items-start justify-center pb-6">
        <p className="w-full text-display-sm text-foreground">
          {t('ideationWidget.ideasShowcase.kickstartYourThinkingWithThese')}
        </p>
      </div>

      {/* Ideas */}
      <div className={`flex flex-col ${exporting ? 'gap-0' : 'gap-20'}`}>
        {ideas.map((idea, index) => (
          <IdeaItem
            key={idea.id}
            idea={idea}
            index={index}
            onSelect={handleIdeaSelect}
            exporting={exporting}
          />
        ))}
      </div>
    </div>
  );
});

interface IdeaItemProps {
  idea: IdeasShowcaseProps['ideas'][number];
  index: number;
  onSelect: (index: number) => void;
  exporting?: boolean;
}

/**
 * IdeaItem - Individual idea container with header
 * Memoized to prevent re-renders when sibling ideas change
 */
const IdeaItem = memo(function IdeaItem({
  idea,
  index,
  onSelect,
  exporting = false,
}: IdeaItemProps) {
  const { t } = useTranslation('contentTest');
  // Memoize the callback to prevent IdeaShowcaseCard re-renders
  const handleExploreIdea = useCallback(() => {
    onSelect(index);
  }, [onSelect, index]);

  // eslint-disable-next-line i18next/no-literal-string -- layout position values
  const imagePosition = index % 2 === 0 ? 'left' : 'right';

  return (
    <div className="flex flex-col">
      {/* Section Header */}
      <div className={`flex w-full items-start ${exporting ? 'pb-6' : 'pb-10'}`}>
        <div className="flex flex-1 items-center gap-4 border-t border-border-light pt-4">
          <p className="text-heading-md font-medium text-foreground">
            {t('ideationWidget.ideasShowcase.ideaNumber', { ideaNumber: index + 1 })}
          </p>
          {exporting && (
            <p className="text-heading-md font-medium text-muted-foreground">{idea.title}</p>
          )}
        </div>
      </div>

      {/* Idea Card — omitted on export, where the list shows titles only. */}
      {!exporting && (
        <IdeaShowcaseCard
          idea={idea}
          imagePosition={imagePosition}
          onExploreIdea={handleExploreIdea}
        />
      )}
    </div>
  );
});
